import * as ImagePicker from "expo-image-picker";

import {
  AHText,
  Box,
  TouchableBox,
  useLayoutContext,
  useTheme,
  AHIcon,
} from "arkh";
import Animated, {
  interpolate,
  useAnimatedStyle,
  SharedValue,
} from "react-native-reanimated";
import { Platform } from "react-native";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { BlurView } from "expo-blur";
import { CameraView, CameraType } from "expo-camera";
import { CameraButton } from "./CameraButton";
import { CircleButton } from "./CircleButton";

import { WidgetNoteText } from "./WidgetNoteText";
import { openAppSettings } from "./openAppSettings";
import { processImage } from "../../../Image/processImage";
import { FormattedMessage } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { WidgetNoteCameraMode_widgetNotePage$key } from "./__generated__/WidgetNoteCameraMode_widgetNotePage.graphql";

import { captureException } from "../../../../common/sentry";
import { useAlert } from "../../../../common/Alert";
import { WidgetNoteSize } from "./__generated__/NoteEdit.graphql";
type Props = {
  uploadImage: (
    localUri: string,
    { width, height }: { width: number; height: number }
  ) => Promise<{
    localImageURI: string;
    imageId: string;
  }>;
  page: WidgetNoteCameraMode_widgetNotePage$key;
  onCancel(): void;
  onSave(image: {
    uri: string;
    translateX: number;
    translateY: number;
    scale: number;
    width: number;
    height: number;
    id: string;
  }): void;
  animation: SharedValue<number>;
  widgetLayout: {
    width: number;
    height: number;
    size: WidgetNoteSize;
  };
};

export function WidgetNoteCameraMode(props: Props) {
  const page = useFragment(
    graphql`
      fragment WidgetNoteCameraMode_widgetNotePage on WidgetNotePage {
        ...WidgetNoteText_page
      }
    `,
    props.page
  );
  const theme = useTheme();
  const layout = useLayoutContext();
  const [hasPermission, setHasPermission] = useState<null | boolean>(null);
  const [type, setType] = useState<CameraType>("back");

  useEffect(() => {
    (async () => {
      const permission = await ImagePicker.requestCameraPermissionsAsync();
      setHasPermission(permission.granted);
    })();
  }, []);

  const animatedControlStyle = useAnimatedStyle(() => {
    return {
      opacity: interpolate(props.animation.value, [0, 1], [0, 1]),
    };
  }, []);

  const cameraRef = useRef<any>(null);
  const isTakingPicture = useRef(false);
  const Alert = useAlert();
  const uploadImage = props.uploadImage;
  const takePicture = useCallback(async () => {
    if (isTakingPicture.current) {
      return;
    }
    isTakingPicture.current = true;
    try {
      const photo = await cameraRef.current?.takePictureAsync({ quality: 1 });
      if (photo) {
        const image = await processImage(photo?.uri);
        const { imageId } = await uploadImage(image.uri, {
          width: image.width,
          height: image.height,
        });
        props.onSave({
          uri: image.uri,
          translateX: 0,
          translateY: 0,
          scale: 1,
          width: image.width,
          height: image.height,
          id: imageId,
        });
      }
      isTakingPicture.current = false;
    } catch (e) {
      isTakingPicture.current = false;

      captureException(e);
      Alert.error("Failed to take picture", "Please try again");
    }
  }, [Alert, props, uploadImage]);

  return (
    <Box
      style={{
        position: "absolute",
        zIndex: 1,
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(255,255,255,0.6)",
      }}
      color="background"
    >
      <BlurView
        intensity={85}
        tint="dark"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: -1,
          height: "100%",
          width: "100%",
        }}
      />
      {hasPermission ? (
        <Box style={{ width: props.widgetLayout.width }}>
          <Box
            style={{
              width: props.widgetLayout.width,
              height: props.widgetLayout.height,
              overflow: "hidden",
              borderRadius: layout.radius.medium,
              justifyContent: "flex-start",
            }}
          >
            <CameraView
              ref={cameraRef}
              style={{
                position: "absolute",
                left: 0,
                top:
                  Platform.OS === "android"
                    ? -(props.widgetLayout.width - props.widgetLayout.height) /
                      2
                    : 0,
                width: props.widgetLayout.width,
                height:
                  Platform.OS === "android"
                    ? props.widgetLayout.width
                    : props.widgetLayout.height,
                justifyContent: "flex-end",
              }}
              facing={type}
            />
            <WidgetNoteText page={page} />
          </Box>
          <Animated.View
            style={[
              {
                position: "absolute",
                bottom: -100,
                width: "100%",
                height: 70,
                zIndex: 5,
                alignItems: "center",
                paddingHorizontal: layout.space.medium,
              },
              animatedControlStyle,
            ]}
          >
            <CircleButton
              onPress={props.onCancel}
              style={{
                top: 10,
                left: layout.space.medium,
              }}
            >
              <AHIcon type="clear" forceSize={30} customColor={theme.surface} />
            </CircleButton>
            <CameraButton size={70} onPress={takePicture} />
            <CircleButton
              onPress={() => {
                setType(type === "back" ? "front" : "back");
              }}
              style={{
                top: 10,
                right: layout.space.medium,
              }}
            >
              <AHIcon
                type="update"
                customColor={theme.surface}
                forceSize={30}
              />
            </CircleButton>
          </Animated.View>
        </Box>
      ) : hasPermission === false ? (
        <Box
          style={{
            width: props.widgetLayout.width,
            height: props.widgetLayout.height,
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
          cornerRadius="medium"
          color="surface"
        >
          <AHText type="title">
            <FormattedMessage
              defaultMessage="No access to camera"
              description="WidgetNoteCameraMode no access to camera title"
            />
          </AHText>
          <Box spaceTop="xxlarge" />
          <TouchableBox
            space="medium"
            borderColor="primary"
            cornerRadius="medium"
            style={{ borderWidth: 2 }}
            onPress={() => openAppSettings()}
          >
            <AHText color="primary" type="title-small">
              <FormattedMessage
                defaultMessage="Enable camera access"
                description="WidgetNoteCameraMode no access to camera enbale camera access button title"
              />
            </AHText>
          </TouchableBox>
          <Box spaceTop="large" />
          <TouchableBox space="medium" onPress={props.onCancel}>
            <AHText color="primary" type="title-small">
              <FormattedMessage
                defaultMessage="Go back"
                description="WidgetNoteCameraMode no access to camera go back button title"
              />
            </AHText>
          </TouchableBox>
        </Box>
      ) : null}
    </Box>
  );
}
